import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAllResumes, deleteResume, duplicateResume, setPrimaryResume } from '../services/resumeService';
import { BASE_URL } from '../services/api';
import '../styles/Resumes.css';

const Resumes = () => {
  const [resumes, setResumes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [openDropdown, setOpenDropdown] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchResumes();
  }, []);

  useEffect(() => {
    const handleClickOutside = () => {
      if (openDropdown) setOpenDropdown(null);
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [openDropdown]);

  const fetchResumes = async () => {
    try {
      setLoading(true);
      const data = await getAllResumes();
      setResumes(data.resumes || []);
    } catch (error) {
      console.error('Error fetching resumes:', error);
      alert('Failed to fetch resumes');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this resume?')) {
      try {
        await deleteResume(id);
        fetchResumes();
        alert('Resume deleted successfully');
      } catch (error) {
        console.error('Error deleting resume:', error);
        alert('Failed to delete resume');
      }
    }
  };

  const handleDuplicate = async (id) => {
    try {
      await duplicateResume(id);
      fetchResumes();
      alert('Resume duplicated successfully');
    } catch (error) {
      console.error('Error duplicating resume:', error);
      alert('Failed to duplicate resume');
    }
  };

  const handleSetPrimary = async (id) => {
    try {
      await setPrimaryResume(id);
      fetchResumes();
      alert('Primary resume updated');
    } catch (error) {
      console.error('Error setting primary resume:', error);
      alert('Failed to set primary resume');
    }
  };

  const handleUnmarkPrimary = async (id) => {
    try {
      const response = await fetch(`${BASE_URL}/api/resume/${id}/unmark-primary`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to unmark primary');
      }
      
      fetchResumes();
      alert('Resume unmarked as primary');
    } catch (error) {
      console.error('Error unmarking primary resume:', error);
      alert(error.message || 'Failed to unmark primary resume');
    }
  };

  const handleUploadResume = async (id, file) => {
    if (!file) return;

    const formData = new FormData();
    formData.append('resume', file);

    try {
      const response = await fetch(`${BASE_URL}/api/resume/${id}/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to upload resume');
      }

      fetchResumes();
      alert('Resume uploaded successfully');
    } catch (error) {
      console.error('Error uploading resume:', error);
      alert(error.message || 'Failed to upload resume');
    }
  };

  const handleDownload = (id, templateId) => {
    window.open(`${BASE_URL}/api/resume/${id}/pdf?templateId=${templateId}`, '_blank');
  };

  const filteredResumes = resumes.filter(resume => {
    if (filter === 'primary') return resume.isPrimary;
    return true;
  });

  const getTemplateColor = (templateId) => {
    const colors = {
      modern: '#2563eb',
      professional: '#059669',
      creative: '#dc2626',
      minimal: '#64748b',
      executive: '#7c3aed',
      technical: '#0891b2',
      academic: '#ea580c',
      classic: '#4b5563',
      bold: '#be123c',
      elegant: '#9333ea'
    };
    return colors[templateId] || '#2563eb';
  };

  if (loading) {
    return (
      <div className="resumes-container">
        <div className="loading">Loading resumes...</div>
      </div>
    );
  }

  return (
    <div className="resumes-container">
      <div className="card">
        <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h2 style={{ color: '#667eea', fontSize: '22px', marginBottom: '4px' }}>📄 My Resumes</h2>
            <p style={{ color: '#718096', fontSize: '13px', margin: 0 }}>
              {resumes.length} resume{resumes.length !== 1 ? 's' : ''} created
            </p>
          </div>
          <button 
            className="btn-create-resume"
            onClick={() => navigate('/resumes/create')}
          >
            <span className="icon">+</span> Create New Resume
          </button>
        </div>

      <div className="resumes-filters">
        <button 
          className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
          onClick={() => setFilter('all')}
        >
          All Resumes ({resumes.length})
        </button>
        <button 
          className={`filter-btn ${filter === 'primary' ? 'active' : ''}`}
          onClick={() => setFilter('primary')}
        >
          Primary ({resumes.filter(r => r.isPrimary).length})
        </button>
      </div>

      {filteredResumes.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📄</div>
          <h3>No resumes yet</h3>
          <p>Create your first resume to get started</p>
          <button 
            className="btn-primary"
            onClick={() => navigate('/resumes/create')}
          >
            Create Resume
          </button>
        </div>
      ) : (
        <div className="table-container">
          <table className="resumes-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Template</th>
                <th>Target Position</th>
                <th>Last Updated</th>
                <th>Status</th>
                <th>Uploaded File</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredResumes.map(resume => (
                <tr key={resume._id} className={resume.isPrimary ? 'primary-row' : ''}>
                  <td className="resume-title-cell">
                    {resume.isPrimary && <span className="primary-star">⭐</span>}
                    <strong>{resume.title}</strong>
                  </td>
                  <td>
                    <span 
                      className="template-badge"
                      style={{ backgroundColor: getTemplateColor(resume.templateId) }}
                    >
                      {resume.templateId}
                    </span>
                  </td>
                  <td>{resume.targetPosition || '—'}</td>
                  <td>{new Date(resume.updatedAt).toLocaleDateString()}</td>
                  <td>
                    {resume.isPrimary && <span className="badge-primary">Primary</span>}
                  </td>
                  <td className="upload-cell">
                    {resume.uploadedResumeUrl ? (
                      <div className="uploaded-file-info">
                        <a 
                          href={resume.uploadedResumeUrl} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="file-link"
                        >
                          📎 View File
                        </a>
                        <span className="upload-date">
                          {new Date(resume.uploadedAt).toLocaleDateString()}
                        </span>
                      </div>
                    ) : (
                      <label className="upload-btn">
                        <input
                          type="file"
                          accept=".pdf,.doc,.docx"
                          onChange={(e) => {
                            if (e.target.files[0]) {
                              handleUploadResume(resume._id, e.target.files[0]);
                              e.target.value = '';
                            }
                          }}
                          style={{ display: 'none' }}
                        />
                        📤 Upload
                      </label>
                    )}
                  </td>
                  <td className="actions-cell">
                    <div className="table-actions">
                      <button 
                        className="btn-action btn-edit"
                        onClick={() => navigate(`/resumes/edit/${resume._id}`)}
                        title="Edit"
                      >
                        Edit
                      </button>
                      <button 
                        className="btn-action btn-preview"
                        onClick={() => navigate(`/resumes/preview/${resume._id}`)}
                        title="Preview"
                      >
                        Preview
                      </button>
                      <button 
                        className="btn-action btn-download"
                        onClick={() => handleDownload(resume._id, resume.templateId)}
                        title="Download PDF"
                      >
                        PDF
                      </button>
                      
                      <div className="dropdown">
                        <button 
                          className="btn-action btn-more"
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenDropdown(openDropdown === resume._id ? null : resume._id);
                          }}
                        >
                          ⋯
                        </button>
                        {openDropdown === resume._id && (
                          <div className="dropdown-menu">
                            {!resume.isPrimary && (
                              <button onClick={() => {
                                handleSetPrimary(resume._id);
                                setOpenDropdown(null);
                              }}>
                                ⭐ Set as Primary
                              </button>
                            )}
                            {resume.isPrimary && (
                              <button onClick={() => {
                                handleUnmarkPrimary(resume._id);
                                setOpenDropdown(null);
                              }}>
                                ⭕ Unmark as Primary
                              </button>
                            )}
                            <button onClick={() => {
                              handleDuplicate(resume._id);
                              setOpenDropdown(null);
                            }}>
                              📋 Duplicate
                            </button>
                            {resume.uploadedResumeUrl && (
                              <label className="dropdown-upload-btn">
                                <input
                                  type="file"
                                  accept=".pdf,.doc,.docx"
                                  onChange={(e) => {
                                    if (e.target.files[0]) {
                                      handleUploadResume(resume._id, e.target.files[0]);
                                      e.target.value = '';
                                      setOpenDropdown(null);
                                    }
                                  }}
                                  style={{ display: 'none' }}
                                />
                                🔄 Re-upload File
                              </label>
                            )}
                            <button 
                              onClick={() => {
                                handleDelete(resume._id);
                                setOpenDropdown(null);
                              }}
                              className="danger"
                            >
                              🗑️ Delete
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      </div>
    </div>
  );
};

export default Resumes;
